import React from 'react';
import { ArrowRight } from 'lucide-react';
import BlurFade from '../primitives/BlurFade';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

/**
 * LifestyleBlock — cinematic full-bleed image with overlay text + CTA.
 *
 * A clip-shade.de inspired interlude that breaks up product-heavy sections
 * and sells the lived-in dream rather than the product itself. The block is
 * intentionally full-bleed (escapes the global rail) to feel like a magazine
 * spread.
 *
 * Props:
 *   testId, mediaSrc, title, sub, cta, hint
 */
export default function LifestyleBlock({ testId, mediaSrc, title, sub, cta, hint }) {
  const { lang, overlay } = useLang();
  return (
    <section id="lifestyle" data-testid={testId} className="relative w-full bg-[#FAF8F2]">
      <div className="relative w-full h-[70vh] min-h-[480px] max-h-[820px] overflow-hidden">
        {/* Image */}
        <img
          src={mediaSrc}
          alt={mediaAlt(overlay, mediaSrc, lang, title || sub || '')}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {/* Soft dark bottom-left gradient so the headline reads on any photo */}
        <div className="absolute inset-0 bg-gradient-to-tr from-black/75 via-black/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Overlay content — bottom-left, on the same rail as the rest of the page */}
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1440px] w-full mx-auto px-5 lg:px-10 pb-12 lg:pb-20">
            <BlurFade>
              <h2 className="font-display text-white text-[40px] sm:text-[60px] lg:text-[84px] leading-[1.02] max-w-3xl drop-shadow-[0_2px_18px_rgba(0,0,0,0.35)]">
                {title}
              </h2>
            </BlurFade>
            {sub && (
              <BlurFade delay={0.1}>
                <p className="mt-4 text-white/90 text-[15.5px] sm:text-[17.5px] max-w-xl leading-relaxed drop-shadow">
                  {sub}
                </p>
              </BlurFade>
            )}
            <BlurFade delay={0.18}>
              <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {cta && (
                  <a
                    href={cta.href}
                    data-testid={cta.testId}
                    data-lead-source={cta.source}
                    className="pill pill-leaf"
                  >
                    {cta.label} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
                {hint && (
                  <span className="text-white/80 text-[12.5px] tracking-wide">
                    {hint}
                  </span>
                )}
              </div>
            </BlurFade>
          </div>
        </div>
      </div>
    </section>
  );
}
